// Pure reconciliation engine — computes a IReconciliationResult from Source A
// (the parent's own uploaded result), Source B (its direct children's results),
// and an optional IReV roll-up. No I/O, no React: fully unit-testable, and the
// exact shape the backend `parent-child-reconciliation` endpoint will return.
//
// Neutral by design: it quantifies and locates divergence; it never decides
// which source is "right".

import { type IChildResultDetail, type IResultPartyVote } from '../../../interface/child-agent';
import {
	DEFAULT_RECON_THRESHOLDS,
	type IReconciliationResult,
	type IReconIRevCoverage,
	type IReconPartyDelta,
	type IReconSource,
	type IReconSourceParty,
	type IReconThresholds,
	type IReconTurnout,
	type IReconTurnoutDelta,
	type IReconWinnerFlip,
	RECON_TURNOUT_LABELS,
	type ReconLevel,
	type ReconSeverity,
	type ReconTurnoutField,
	type ReconVerdict,
} from '../../../interface/reconciliation';

const TURNOUT_FIELDS: ReconTurnoutField[] = [
	'votersOnRegister',
	'accreditedVoters',
	'ballotPapersIssuedToPoolingUnit',
	'unusedBallotPapers',
	'rejectedBallot',
	'totalValidVotes',
	'totalUsedBallotPapers',
];

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);

/** Stable identity for a party across sources: id first, then short/long name. */
export const partyKey = (p: { partyId?: string | number; partyShortName?: string; partyName?: string }): string => {
	if (p.partyId !== undefined && p.partyId !== null && String(p.partyId).trim() !== '') return `id:${String(p.partyId)}`;
	if (p.partyShortName) return `sn:${p.partyShortName.trim().toUpperCase()}`;
	return `nm:${(p.partyName || '').trim().toUpperCase()}`;
};

/** Best display label for a party (short name preferred, else full name). */
export const partyLabel = (p: { partyShortName?: string; partyName?: string }): string =>
	(p.partyShortName || p.partyName || '').trim();

/**
 * Severity for a single figure. A winner-flip is handled by the caller (always
 * critical). `absFloor` guarantees a 1-vote rounding difference never escalates.
 */
export const severityOf = (aValue: number, bValue: number, t: IReconThresholds = DEFAULT_RECON_THRESHOLDS): ReconSeverity => {
	const deltaAbs = Math.abs(bValue - aValue);
	if (deltaAbs <= t.absFloor) return 'reconciled';
	const denom = Math.max(Math.abs(aValue), Math.abs(bValue), 1);
	const pct = deltaAbs / denom;
	if (pct <= t.okPct) return 'reconciled';
	if (pct <= t.minorPct) return 'minor';
	return 'critical';
};

const deltaPctOf = (aValue: number, bValue: number): number => {
	const denom = Math.max(Math.abs(aValue), Math.abs(bValue), 1);
	return Math.abs(bValue - aValue) / denom;
};

/** The party with the most votes in a source (null when the source has none). */
const leaderOf = (parties: IReconSourceParty[]): IReconSourceParty | null => {
	let best: IReconSourceParty | null = null;
	for (const p of parties) {
		if (num(p.votes) <= 0) continue;
		if (!best || num(p.votes) > num(best.votes)) best = p;
	}
	return best;
};

/** Convert one result detail into a normalized source (turnout + parties). */
export const detailToSource = (detail: IChildResultDetail | null | undefined): IReconSource => {
	const turnout: Partial<IReconTurnout> = {};
	if (detail) {
		for (const f of TURNOUT_FIELDS) turnout[f] = num(detail[f as keyof IChildResultDetail]);
	}
	const parties: IReconSourceParty[] = (detail?.results ?? []).map((r: IResultPartyVote) => ({
		partyId: r.partyId,
		partyName: r.partyName,
		partyShortName: r.partyShortName,
		votes: num(r.votes),
	}));
	const leader = leaderOf(parties);
	return {
		turnout,
		parties,
		leadingPartyId: leader ? leader.partyId : null,
		leadingPartyName: leader ? partyLabel(leader) : null,
	};
};

/** Sum many result details into a single aggregated source (Source B / IReV). */
export const sumDetails = (details: (IChildResultDetail | null | undefined)[]): IReconSource => {
	const turnout: Partial<IReconTurnout> = {};
	for (const f of TURNOUT_FIELDS) turnout[f] = 0;

	const byParty = new Map<string, IReconSourceParty>();
	for (const detail of details) {
		if (!detail) continue;
		for (const f of TURNOUT_FIELDS) turnout[f] = num(turnout[f]) + num(detail[f as keyof IChildResultDetail]);
		for (const r of detail.results ?? []) {
			const key = partyKey(r);
			const existing = byParty.get(key);
			if (existing) existing.votes += num(r.votes);
			else
				byParty.set(key, {
					partyId: r.partyId,
					partyName: r.partyName,
					partyShortName: r.partyShortName,
					votes: num(r.votes),
				});
		}
	}
	const parties = [...byParty.values()];
	const leader = leaderOf(parties);
	return {
		turnout,
		parties,
		leadingPartyId: leader ? leader.partyId : null,
		leadingPartyName: leader ? partyLabel(leader) : null,
	};
};

/** Union party lookup for a source, keyed by stable party identity. */
const partyMap = (source: IReconSource): Map<string, IReconSourceParty> => {
	const m = new Map<string, IReconSourceParty>();
	for (const p of source.parties) m.set(partyKey(p), p);
	return m;
};

interface IComputeArgs {
	level: ReconLevel;
	entityCode: string;
	entityName?: string;
	/** The parent's own uploaded result (Source A), or null if not uploaded yet. */
	sourceADetail: IChildResultDetail | null | undefined;
	/** The direct children's uploaded results (used to build Source B). */
	childDetails: (IChildResultDetail | null | undefined)[];
	completeness: { childrenTotal: number; childrenUploaded: number };
	/** Available leaf-level IReV result details across the subtree (optional). */
	irevDetails?: (IChildResultDetail | null | undefined)[];
	irevCoverage?: IReconIRevCoverage | null;
	thresholds?: IReconThresholds;
}

/**
 * Compute the full reconciliation. Verdict is forced to `awaitingCoverage`
 * whenever child coverage is below 100% (or there are no children / no Source A),
 * so a structurally-incomplete Source B can never masquerade as a discrepancy.
 */
export const computeReconciliation = (args: IComputeArgs): IReconciliationResult => {
	const t = args.thresholds ?? DEFAULT_RECON_THRESHOLDS;
	const sourceA = detailToSource(args.sourceADetail);
	const sourceB = sumDetails(args.childDetails);
	const irev = args.irevDetails?.length ? sumDetails(args.irevDetails) : null;

	const aParties = partyMap(sourceA);
	const bParties = partyMap(sourceB);
	const irevParties = irev ? partyMap(irev) : null;

	// Union of all party keys across A and B (a party present in only one source
	// is itself a divergence worth surfacing).
	const allKeys = new Set<string>([...aParties.keys(), ...bParties.keys()]);

	const partyDeltas: IReconPartyDelta[] = [...allKeys].map((key) => {
		const a = aParties.get(key);
		const b = bParties.get(key);
		const iv = irevParties?.get(key);
		const ref = a ?? b ?? iv!;
		const aVotes = num(a?.votes);
		const bVotes = num(b?.votes);
		const delta = bVotes - aVotes;
		return {
			partyId: ref.partyId,
			partyName: ref.partyName,
			partyShortName: ref.partyShortName,
			aVotes,
			bVotes,
			irevVotes: iv ? num(iv.votes) : null,
			delta,
			deltaAbs: Math.abs(delta),
			deltaPct: deltaPctOf(aVotes, bVotes),
			severity: severityOf(aVotes, bVotes, t),
		};
	});

	const turnoutDeltas: IReconTurnoutDelta[] = TURNOUT_FIELDS.map((field) => {
		const aValue = num(sourceA.turnout[field]);
		const bValue = num(sourceB.turnout[field]);
		const delta = bValue - aValue;
		return {
			field,
			label: RECON_TURNOUT_LABELS[field],
			aValue,
			bValue,
			irevValue: irev ? num(irev.turnout[field]) : null,
			delta,
			deltaAbs: Math.abs(delta),
			deltaPct: deltaPctOf(aValue, bValue),
			severity: severityOf(aValue, bValue, t),
		};
	});

	const winnerFlip: IReconWinnerFlip = {
		flipped:
			sourceA.leadingPartyId != null &&
			sourceB.leadingPartyId != null &&
			partyKey({ partyId: sourceA.leadingPartyId, partyName: sourceA.leadingPartyName ?? undefined }) !==
				partyKey({ partyId: sourceB.leadingPartyId, partyName: sourceB.leadingPartyName ?? undefined }),
		aLeaderPartyId: sourceA.leadingPartyId,
		aLeaderName: sourceA.leadingPartyName,
		bLeaderPartyId: sourceB.leadingPartyId,
		bLeaderName: sourceB.leadingPartyName,
	};

	const sourceAUploaded = !!args.sourceADetail;
	const { childrenTotal, childrenUploaded } = args.completeness;
	const covered = childrenTotal > 0 && childrenUploaded >= childrenTotal;

	let verdict: ReconVerdict;
	if (!covered || !sourceAUploaded) {
		verdict = 'awaitingCoverage';
	} else if (winnerFlip.flipped) {
		verdict = 'critical';
	} else {
		const worst = worstSeverity([...partyDeltas.map((d) => d.severity), ...turnoutDeltas.map((d) => d.severity)]);
		verdict = worst;
	}

	return {
		verdict,
		level: args.level,
		entityCode: args.entityCode,
		entityName: args.entityName,
		completeness: args.completeness,
		irevCoverage: args.irevCoverage ?? null,
		sourceA,
		sourceB,
		irev,
		partyDeltas: sortDeltas(partyDeltas),
		turnoutDeltas,
		winnerFlip,
		ruleVersion: t.ruleVersion,
		sourceAUploaded,
	};
};

/** Compact per-child summary for the drill-down tree (contribution + local flip). */
export interface IChildSummary {
	validVotes: number;
	tallied: number;
	leaderKey: string | null;
	leaderLabel: string | null;
	// In-result mismatch: the child's own party sum ≠ its recorded valid votes.
	mismatch: boolean;
}

export const summarizeChild = (detail: IChildResultDetail | null | undefined): IChildSummary => {
	const source = detailToSource(detail);
	const validVotes = num(source.turnout.totalValidVotes);
	const tallied = source.parties.reduce((acc, p) => acc + num(p.votes), 0);
	return {
		validVotes,
		tallied,
		leaderKey:
			source.leadingPartyId != null
				? partyKey({ partyId: source.leadingPartyId, partyName: source.leadingPartyName ?? undefined })
				: null,
		leaderLabel: source.leadingPartyName ?? null,
		mismatch: validVotes > 0 && tallied !== validVotes,
	};
};

const SEVERITY_RANK: Record<ReconSeverity, number> = { reconciled: 0, minor: 1, critical: 2 };

/** The most severe band in a list (reconciled when empty). */
export const worstSeverity = (list: ReconSeverity[]): ReconSeverity =>
	list.reduce<ReconSeverity>((worst, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[worst] ? s : worst), 'reconciled');

/** Sort party deltas: most severe first, then largest absolute gap. */
const sortDeltas = (deltas: IReconPartyDelta[]): IReconPartyDelta[] =>
	[...deltas].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity] || b.deltaAbs - a.deltaAbs);
